// Vouchers list — all posted vouchers with full filter set.
// Phase 5.8: adds date range presets, party filter, amount range, include-cancelled,
// CN/DN pills, and reset button. Mirrors Day Book filter UX.

import { useMemo, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { DataTable, type Column } from '@/components/DataTable';
import { Money } from '@/components/primitives/Money';
import { Button } from '@/components/primitives/Button';
import { useVouchers, useCancelVoucher, type VoucherRow } from '@/api/vouchers';
import { ledgers } from '@/api/masters';
import { formatDateDDMMMYYYY, displayNarration, todayIso as todayISO, fyStartIso as fyStart, daysAgoIso as daysAgo } from '@/utils/format';
import { cn } from '@/lib/cn';

type BaseFilter = 'ALL' | 'SALES' | 'PURCHASE' | 'RECEIPT' | 'PAYMENT' | 'CONTRA' | 'JOURNAL' | 'CREDIT_NOTE' | 'DEBIT_NOTE';
const FILTERS: Array<{ key: BaseFilter; label: string }> = [
  { key: 'ALL',         label: 'All' },
  { key: 'SALES',       label: 'Sales' },
  { key: 'PURCHASE',    label: 'Purchase' },
  { key: 'RECEIPT',     label: 'Receipt' },
  { key: 'PAYMENT',     label: 'Payment' },
  { key: 'CONTRA',      label: 'Contra' },
  { key: 'JOURNAL',     label: 'Journal' },
  { key: 'CREDIT_NOTE', label: 'CN' },
  { key: 'DEBIT_NOTE',  label: 'DN' },
];

const PRESETS: Array<{ key: string; label: string; range: () => { from: string; to: string } }> = [
  { key: '7d',  label: '7d',  range: () => ({ from: daysAgo(6),  to: todayISO() }) },
  { key: '30d', label: '30d', range: () => ({ from: daysAgo(29), to: todayISO() }) },
  { key: '90d', label: '90d', range: () => ({ from: daysAgo(89), to: todayISO() }) },
  { key: 'fy',  label: 'FY',  range: () => ({ from: fyStart(),   to: todayISO() }) },
];

export function VouchersListPage() {
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);
  const [q, setQ] = useState('');
  const [filter, setFilter] = useState<BaseFilter>('ALL');
  const [fromDate, setFromDate] = useState<string>('');
  const [toDate, setToDate] = useState<string>('');
  const [partyLedgerId, setPartyLedgerId] = useState<string>('');
  const [minAmount, setMinAmount] = useState<string>('');
  const [maxAmount, setMaxAmount] = useState<string>('');
  const [includeCancelled, setIncludeCancelled] = useState<boolean>(false);

  const ledgerListQ = ledgers.useList({ pageSize: 500 });
  const partyLedgerOptions = (ledgerListQ.data?.rows ?? []).filter((l: any) => {
    const bl = l.group?.behavesLike;
    return bl === 'SUNDRY_DEBTORS' || bl === 'SUNDRY_CREDITORS' || l.billByBill === true;
  });

  const list = useVouchers({
    page, pageSize, q,
    ...(filter !== 'ALL' && { baseTypeCode: filter }),
    ...(fromDate && { fromDate }),
    ...(toDate && { toDate }),
    ...(partyLedgerId && { partyLedgerId }),
    ...(minAmount && { minAmount: Number(minAmount) }),
    ...(maxAmount && { maxAmount: Number(maxAmount) }),
    ...(includeCancelled && { includeCancelled: true }),
  });

  function applyPreset(r: { from: string; to: string }) { setFromDate(r.from); setToDate(r.to); setPage(1); }
  function resetFilters() {
    setFilter('ALL'); setFromDate(''); setToDate(''); setPartyLedgerId(''); setMinAmount(''); setMaxAmount('');
    setIncludeCancelled(false); setQ(''); setPage(1);
  }
  const hasFilters = filter !== 'ALL' || fromDate || toDate || partyLedgerId || minAmount || maxAmount || includeCancelled || q;

  // Multi-select (Phase 8.7) — selection is by voucher id, persists across pages
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const rows = list.data?.rows ?? [];
  const visibleIds = useMemo(() => rows.map((v) => v.id), [rows]);
  const allOnPageSelected = visibleIds.length > 0 && visibleIds.every((id) => selected.has(id));
  function toggle(id: string) {
    setSelected((s) => { const n = new Set(s); if (n.has(id)) n.delete(id); else n.add(id); return n; });
  }
  function selectAllOnPage() {
    setSelected((s) => { const n = new Set(s); visibleIds.forEach((id) => n.add(id)); return n; });
  }
  function clearSelection() { setSelected(new Set()); }

  const qc = useQueryClient();
  const cancelMut = useCancelVoucher();
  const [bulkBusy, setBulkBusy] = useState(false);
  async function bulkCancel() {
    const ids = Array.from(selected);
    if (ids.length === 0) return;
    const reason = window.prompt(`Cancel ${ids.length} voucher${ids.length === 1 ? '' : 's'}? Enter a reason (required). This writes reversing entries; originals stay in the audit log.`);
    if (!reason || !reason.trim()) return;
    setBulkBusy(true);
    let ok = 0, failed = 0;
    for (const id of ids) {
      try { await cancelMut.mutateAsync({ id, reason: reason.trim() }); ok++; } catch { failed++; }
    }
    setBulkBusy(false);
    clearSelection();
    qc.invalidateQueries({ queryKey: ['vouchers'] });
    qc.invalidateQueries({ queryKey: ['reports'] });
    window.alert(`${ok} cancelled${failed > 0 ? `, ${failed} failed (likely already cancelled or locked).` : '.'}`);
  }

  const columns: Column<VoucherRow>[] = [
    {
      key: 'select', header: '', width: '36px', align: 'center',
      render: (v) => (
        <input
          type="checkbox"
          checked={selected.has(v.id)}
          onChange={(e) => { e.stopPropagation(); toggle(v.id); }}
          onClick={(e) => e.stopPropagation()}
          aria-label={`Select voucher ${v.number}`}
        />
      ),
    },
    {
      key: 'number', header: 'Voucher No.', width: '140px',
      render: (v) => (
        <a href={`/vouchers/${v.id}`} className={cn('font-mono text-cobalt hover:underline', v.cancelledAt && 'line-through opacity-60')}>{v.number}</a>
      ),
    },
    {
      key: 'date', header: 'Date', width: '100px',
      render: (v) => <span className="font-mono text-caption text-slate">{formatDateDDMMMYYYY(new Date(v.date))}</span>,
    },
    {
      key: 'type', header: 'Type', width: '110px',
      render: (v) => (
        <span className="inline-flex items-center h-5 px-2 rounded-sm bg-cobalt/10 text-cobalt text-nano font-medium uppercase tracking-[0.1em]">
          {v.voucherType?.abbreviation ?? v.voucherType?.code ?? '—'}
        </span>
      ),
    },
    {
      key: 'party', header: 'Party / Narration', width: 'minmax(220px,2fr)',
      render: (v) => (
        <div className="flex flex-col">
          <span className="text-obsidian">{v.partyLedger?.name ?? (v.narration?.startsWith('[BANK:') ? 'Bank import' : '—')}</span>
          {v.narration && <span className="truncate text-caption text-cool">{displayNarration(v.narration)}</span>}
        </div>
      ),
    },
    { key: 'subTotal', header: 'Taxable', width: '110px', align: 'right', numeric: true,
      render: (v) => <Money amount={Number(v.subTotal)} className="text-body-sm" /> },
    { key: 'tax', header: 'Tax', width: '90px', align: 'right', numeric: true,
      render: (v) => <Money amount={Number(v.totalTaxAmount)} className="text-body-sm text-cool" /> },
    { key: 'total', header: 'Total', width: '130px', align: 'right', numeric: true,
      render: (v) => <Money amount={Number(v.totalAmount)} important className="text-body-sm" /> },
    {
      key: 'status', header: '', width: '70px', align: 'center',
      render: (v) => (v.cancelledAt
        ? <span className="inline-flex items-center h-5 px-2 rounded-sm bg-crimson/10 text-crimson text-nano font-medium uppercase tracking-[0.1em]">Cancelled</span>
        : <span className="text-emerald" aria-label="Posted">●</span>
      ),
    },
    {
      key: 'pdf', header: '', width: '60px', align: 'center',
      render: (v) => (
        <a
          href={`/api/vouchers/${encodeURIComponent(v.id)}/pdf?template=raaj-classic`}
          target="_blank"
          rel="noopener noreferrer"
          onClick={(e) => e.stopPropagation()}
          className="inline-flex items-center justify-center h-6 w-6 rounded-sm border border-mist text-cool hover:border-cobalt hover:text-cobalt"
          title="Download Raaj Classic PDF"
        >
          ⤓
        </a>
      ),
    },
  ];

  return (
    <>
      <DataTable<VoucherRow>
        title="Vouchers"
        subtitle="Every posted voucher in the active company"
        columns={columns}
        rows={list.data?.rows ?? []}
        rowKey={(v) => v.id}
        pagination={list.data?.pagination}
        onPageChange={setPage}
        onPageSizeChange={(s) => { setPageSize(s); setPage(1); }}
        search={q}
        onSearchChange={(v) => { setQ(v); setPage(1); }}
        isLoading={list.isLoading}
        error={list.error instanceof Error ? list.error.message : null}
        onCreate={() => { window.location.href = '/vouchers/new/sales'; }}
        createLabel="New Sale"
        onEdit={(v) => { window.location.href = `/vouchers/${v.id}`; }}
        emptyMessage="No vouchers match the current filters. Click New Sale or reset filters."
        rightToolbar={
          <div className="flex flex-wrap items-center gap-2">
            <div className="hidden md:flex items-center gap-1 rounded-md border border-mist bg-frost p-1">
              {FILTERS.map((f) => (
                <button key={f.key} type="button" onClick={() => { setFilter(f.key); setPage(1); }}
                  className={cn('rounded-sm px-2.5 py-1 text-caption transition',
                    filter === f.key ? 'bg-cobalt text-snow' : 'text-slate hover:bg-haze')}>
                  {f.label}
                </button>
              ))}
            </div>
            <a href="/vouchers/new/purchase" className="inline-flex items-center gap-1 rounded-md border border-mist bg-haze px-3 py-1.5 text-body-sm text-obsidian hover:border-cobalt">+ Purchase</a>
            <a href="/vouchers/new/receipt" className="inline-flex items-center gap-1 rounded-md border border-mist bg-haze px-3 py-1.5 text-body-sm text-obsidian hover:border-cobalt">+ Receipt</a>
            <a href="/vouchers/new/payment" className="inline-flex items-center gap-1 rounded-md border border-mist bg-haze px-3 py-1.5 text-body-sm text-obsidian hover:border-cobalt">+ Payment</a>
            <a href="/vouchers/new/contra" className="hidden lg:inline-flex items-center gap-1 rounded-md border border-mist bg-haze px-3 py-1.5 text-body-sm text-obsidian hover:border-cobalt">+ Contra</a>
            <a href="/vouchers/new/journal" className="hidden lg:inline-flex items-center gap-1 rounded-md border border-mist bg-haze px-3 py-1.5 text-body-sm text-obsidian hover:border-cobalt">+ Journal</a>
            <a href="/vouchers/new/credit-note" className="hidden xl:inline-flex items-center gap-1 rounded-md border border-mist bg-haze px-3 py-1.5 text-body-sm text-obsidian hover:border-cobalt">+ CN</a>
            <a href="/vouchers/new/debit-note" className="hidden xl:inline-flex items-center gap-1 rounded-md border border-mist bg-haze px-3 py-1.5 text-body-sm text-obsidian hover:border-cobalt">+ DN</a>
          </div>
        }
        underToolbar={
          <div className="flex flex-wrap items-center gap-2 border-b border-mist px-4 py-2 bg-haze/70">
            <div className="flex items-center gap-1 rounded-md border border-mist bg-frost p-1">
              {PRESETS.map((p) => (
                <button key={p.key} type="button" onClick={() => applyPreset(p.range())}
                  className="rounded-sm px-2 py-0.5 text-caption text-slate hover:bg-haze">
                  {p.label}
                </button>
              ))}
            </div>
            <label className="flex items-center gap-1 text-caption text-slate">
              From <input type="date" value={fromDate} max={toDate || undefined} onChange={(e) => { setFromDate(e.target.value); setPage(1); }}
                className="rounded-sm border border-mist bg-haze px-2 py-1 text-body-sm focus:border-cobalt/60 focus:outline-none focus:shadow-focus" />
            </label>
            <label className="flex items-center gap-1 text-caption text-slate">
              To <input type="date" value={toDate} min={fromDate || undefined} max={todayISO()} onChange={(e) => { setToDate(e.target.value); setPage(1); }}
                className="rounded-sm border border-mist bg-haze px-2 py-1 text-body-sm focus:border-cobalt/60 focus:outline-none focus:shadow-focus" />
            </label>
            <select value={partyLedgerId} onChange={(e) => { setPartyLedgerId(e.target.value); setPage(1); }}
              className="rounded-sm border border-mist bg-haze px-2 py-1 text-body-sm focus:border-cobalt/60 focus:outline-none focus:shadow-focus">
              <option value="">All parties</option>
              {partyLedgerOptions.map((l) => <option key={l.id} value={l.id}>{l.name}</option>)}
            </select>
            <input value={minAmount} onChange={(e) => { setMinAmount(e.target.value); setPage(1); }} placeholder="Min ₹" type="number" min={0}
              className="w-24 rounded-sm border border-mist bg-haze px-2 py-1 text-body-sm focus:border-cobalt/60 focus:outline-none focus:shadow-focus" />
            <input value={maxAmount} onChange={(e) => { setMaxAmount(e.target.value); setPage(1); }} placeholder="Max ₹" type="number" min={0}
              className="w-24 rounded-sm border border-mist bg-haze px-2 py-1 text-body-sm focus:border-cobalt/60 focus:outline-none focus:shadow-focus" />
            <label className="flex items-center gap-1 text-caption text-slate">
              <input type="checkbox" checked={includeCancelled} onChange={(e) => { setIncludeCancelled(e.target.checked); setPage(1); }} />
              Include cancelled
            </label>
            <a
              href={(() => {
                const sp = new URLSearchParams();
                if (filter !== 'ALL') sp.set('baseTypeCode', filter);
                if (fromDate) sp.set('fromDate', fromDate);
                if (toDate) sp.set('toDate', toDate);
                if (partyLedgerId) sp.set('partyLedgerId', partyLedgerId);
                if (minAmount) sp.set('minAmount', minAmount);
                if (maxAmount) sp.set('maxAmount', maxAmount);
                if (includeCancelled) sp.set('includeCancelled', 'true');
                if (q) sp.set('q', q);
                const qs = sp.toString();
                return `/api/vouchers/export.csv${qs ? `?${qs}` : ''}`;
              })()}
              title="Download every voucher matching the current filters (capped at 50k rows)"
              className="inline-flex items-center gap-1 rounded-md border border-mist bg-haze px-2.5 py-1 text-caption text-slate hover:border-cobalt hover:text-cobalt"
            >
              ⤓ CSV
            </a>
            {hasFilters && <Button variant="ghost" onClick={resetFilters}>Reset</Button>}
            {/* Bulk-action strip — appears only when >0 rows selected */}
            {selected.size > 0 && (
              <div className="ml-auto flex items-center gap-2 rounded-md border border-cobalt/40 bg-cobalt/[0.05] px-3 py-1">
                <span className="text-caption text-cobalt font-medium">{selected.size} selected</span>
                {!allOnPageSelected && visibleIds.length > 0 && (
                  <button type="button" onClick={selectAllOnPage} className="text-caption text-cobalt hover:underline">+ all on page</button>
                )}
                <Button variant="danger" onClick={bulkCancel} loading={bulkBusy}>Cancel selected</Button>
                <button type="button" onClick={clearSelection} className="text-caption text-cool hover:text-obsidian">Clear</button>
              </div>
            )}
          </div>
        }
      />
    </>
  );
}
